import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiMessageSquare, FiSend, FiUsers, FiClock, FiCheck, FiX, FiAlertCircle, FiFilter } from 'react-icons/fi';
import { FaPhone, FaSms, FaHistory, FaUserCheck } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import smsService from '../../services/smsService';

const SMSManager = () => {
  const [activeTab, setActiveTab] = useState('compose');
  const [recipients, setRecipients] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [message, setMessage] = useState({
    type: 'individual',
    recipient: '',
    recipientList: [],
    template: '',
    customMessage: '',
    scheduledTime: null
  });

  const [filters, setFilters] = useState({
    customerType: 'all',
    leadStatus: 'all',
    appointmentStatus: 'upcoming'
  });

  useEffect(() => {
    loadData();
    loadMessageHistory();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load customers
      const { data: customerData } = await supabase
        .from('customers')
        .select('id, first_name, last_name, phone, email')
        .order('created_at', { ascending: false });
      setCustomers(customerData || []);

      // Load leads
      const { data: leadData } = await supabase
        .from('leads')
        .select('id, first_name, last_name, phone, email, status')
        .order('created_at', { ascending: false });
      setLeads(leadData || []);

      // Load appointments
      const { data: appointmentData } = await supabase
        .from('service_appointments')
        .select('*')
        .gte('appointment_date', new Date().toISOString().split('T')[0])
        .order('appointment_date');
      setAppointments(appointmentData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessageHistory = () => {
    const history = smsService.getMessageHistory();
    setMessageHistory(history);
  };

  const messageTemplates = [
    { id: 'appointment_reminder', name: 'Appointment Reminder', category: 'Service' },
    { id: 'payment_reminder', name: 'Payment Reminder', category: 'Finance' },
    { id: 'lead_followup', name: 'Lead Follow-up', category: 'Sales' },
    { id: 'service_complete', name: 'Service Complete', category: 'Service' },
    { id: 'promotional', name: 'Promotional Offer', category: 'Marketing' },
    { id: 'test_drive', name: 'Test Drive Confirmation', category: 'Sales' },
    { id: 'document_reminder', name: 'Document Reminder', category: 'Admin' },
    { id: 'deal_update', name: 'Deal Status Update', category: 'Sales' },
    { id: 'welcome', name: 'Welcome Message', category: 'Customer' },
    { id: 'custom', name: 'Custom Message', category: 'Other' }
  ];

  const handleSendSMS = async () => {
    if (!message.recipient && message.recipientList.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    if (!message.customMessage && !message.template) {
      alert('Please enter a message or select a template');
      return;
    }

    setSending(true);
    try {
      const messageText = message.customMessage || getTemplateMessage(message.template);
      
      if (message.type === 'individual') {
        const result = await smsService.sendSMS(message.recipient, messageText);
        if (result.success) {
          alert('SMS sent successfully!');
          resetForm();
        } else {
          alert(`Failed to send SMS: ${result.error}`);
        }
      } else {
        // Bulk send
        const results = await smsService.sendBulkSMS(message.recipientList, messageText);
        const successCount = results.filter(r => r.success).length;
        alert(`Sent ${successCount} of ${results.length} messages successfully`);
        resetForm();
      }
      
      loadMessageHistory();
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const getTemplateMessage = (templateId) => {
    // This would normally fetch from database or use predefined templates
    const templates = {
      appointment_reminder: 'Reminder: Your appointment is tomorrow. Reply CONFIRM to confirm.',
      payment_reminder: 'Your payment is due. Please contact us to arrange payment.',
      lead_followup: 'Thank you for your interest! We have great deals available.',
      service_complete: 'Your vehicle is ready for pickup!',
      promotional: 'Special offer this week only! Visit us for details.',
      test_drive: 'Your test drive is confirmed. See you soon!',
      document_reminder: 'Please bring the required documents to your appointment.',
      deal_update: 'Good news about your deal! Please contact us.',
      welcome: 'Welcome to Paradise-Performance! Thank you for your business.'
    };
    return templates[templateId] || '';
  };

  const resetForm = () => {
    setMessage({
      type: 'individual',
      recipient: '',
      recipientList: [],
      template: '',
      customMessage: '',
      scheduledTime: null
    });
  };

  const handleRecipientSelection = (phone, checked) => {
    if (checked) {
      setMessage({
        ...message,
        recipientList: [...message.recipientList, phone]
      });
    } else {
      setMessage({
        ...message,
        recipientList: message.recipientList.filter(p => p !== phone)
      });
    }
  };

  const getFilteredRecipients = () => {
    let allRecipients = [];

    // Add customers
    if (filters.customerType === 'all' || filters.customerType === 'customers') {
      allRecipients = [...allRecipients, ...customers.map(c => ({
        id: c.id,
        name: `${c.first_name} ${c.last_name}`,
        phone: c.phone,
        type: 'Customer',
        email: c.email
      }))];
    }

    // Add leads
    if (filters.customerType === 'all' || filters.customerType === 'leads') {
      const filteredLeads = filters.leadStatus === 'all' 
        ? leads 
        : leads.filter(l => l.status === filters.leadStatus);
      
      allRecipients = [...allRecipients, ...filteredLeads.map(l => ({
        id: l.id,
        name: `${l.first_name} ${l.last_name}`,
        phone: l.phone,
        type: 'Lead',
        status: l.status,
        email: l.email
      }))];
    }

    // Remove duplicates and filter out empty phones
    return allRecipients.filter(r => r.phone);
  };

  return (
    <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">SMS Manager</h2>
            <p className="text-gray-600">Send text messages to customers and manage SMS campaigns</p>
          </div>
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FaSms} className="w-8 h-8 text-primary-600" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-6 mt-6 border-b">
          {['compose', 'templates', 'bulk', 'scheduled', 'history'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-2 px-1 font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'compose' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Phone Number
              </label>
              <input
                type="tel"
                value={message.recipient}
                onChange={(e) => setMessage({...message, recipient: e.target.value})}
                placeholder="(940) 555-1234"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Template
              </label>
              <select
                value={message.template}
                onChange={(e) => setMessage({...message, template: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a template...</option>
                {messageTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.category})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Text
              </label>
              <textarea
                value={message.customMessage || getTemplateMessage(message.template)}
                onChange={(e) => setMessage({...message, customMessage: e.target.value})}
                rows={4}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Type your message here..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {(message.customMessage || getTemplateMessage(message.template)).length}/160 characters
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
              <button
                onClick={handleSendSMS}
                disabled={sending}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Sending...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                    Send SMS
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'bulk' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
              <select
                value={filters.customerType}
                onChange={(e) => setFilters({...filters, customerType: e.target.value})}
                className="px-3 py-2 border rounded"
              >
                <option value="all">All Contacts</option>
                <option value="customers">Customers Only</option>
                <option value="leads">Leads Only</option>
              </select>

              {filters.customerType !== 'customers' && (
                <select
                  value={filters.leadStatus}
                  onChange={(e) => setFilters({...filters, leadStatus: e.target.value})}
                  className="px-3 py-2 border rounded"
                >
                  <option value="all">All Lead Status</option>
                  <option value="new">New Leads</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                </select>
              )}
            </div>

            {/* Recipients List */}
            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <table>
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          const allPhones = getFilteredRecipients().map(r => r.phone);
                          setMessage({
                            ...message,
                            recipientList: e.target.checked ? allPhones : []
                          });
                        }}
                      />
                    </th>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-left">Phone</th>
                    <th className="px-4 py-2 text-left">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredRecipients().map(recipient => (
                    <tr key={recipient.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={message.recipientList.includes(recipient.phone)}
                          onChange={(e) => handleRecipientSelection(recipient.phone, e.target.checked)}
                        />
                      </td>
                      <td className="px-4 py-2">{recipient.name}</td>
                      <td className="px-4 py-2">{recipient.phone}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 text-xs rounded ${
                          recipient.type === 'Customer' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {recipient.type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                {message.recipientList.length} recipients selected
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setMessage({...message, recipientList: []})}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear Selection
                </button>
                <button
                  onClick={() => setMessage({...message, type: 'bulk'})}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Compose Bulk Message
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {messageTemplates.map(template => (
              <div key={template.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold">{template.name}</h3>
                  <span className="px-2 py-1 text-xs bg-gray-100 rounded">
                    {template.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  {getTemplateMessage(template.id)}
                </p>
                <button
                  onClick={() => setMessage({...message, template: template.id})}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Use Template →
                </button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'scheduled' && (
          <div className="text-center py-12">
            <SafeIcon icon={FiClock} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-[15px] font-semibold text-adm-text-hi mb-2">Scheduled Messages</h3>
            <p className="text-gray-600">Schedule SMS messages to be sent at a future time</p>
            <button 
              onClick={() => {
                setActiveTab('compose');
                setMessage({...message, scheduledTime: new Date().toISOString()});
              }}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
              Schedule New Message
            </button>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            <h3 className="font-semibold">Message History</h3>
            {messageHistory.length === 0 ? (
              <p className="text-gray-500">No messages sent yet</p>
            ) : (
              <div className="space-y-2">
                {messageHistory.map((msg, index) => (
                  <div key={index} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{msg.to}</p>
                        <p className="text-sm text-gray-600">{msg.message}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {new Date(msg.timestamp).toLocaleString()}
                        </p>
                        <span className={`inline-block px-2 py-1 text-xs rounded ${
                          msg.status === 'sent' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {msg.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SMSManager;
